import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import {
  findSpecialty,
  getCoursesForSpecialty,
  getCoursePath,
  resolveLegacyCoursePath,
} from '../utils/formationCatalog';
import { getMediaUrl } from '../utils/mediaUrl';

const FormationSpecialty = () => {
  const { specialtySlug } = useParams();
  const navigate = useNavigate();
  const { getContent } = useContent();
  const pageHeader = getContent('formations.pageHeader') || {};

  const specialty = findSpecialty(getContent, specialtySlug);
  const courses = specialty ? getCoursesForSpecialty(getContent, specialty.id) : [];
  const specialtyPathSlug = specialty?.slug || specialty?.id;

  useEffect(() => {
    if (specialty) return;

    const legacyPath = resolveLegacyCoursePath(getContent, specialtySlug);
    if (legacyPath && legacyPath !== `/formations/${specialtySlug}`) {
      navigate(legacyPath, { replace: true });
      return;
    }

    navigate('/formations', { replace: true });
  }, [specialty, specialtySlug, getContent, navigate]);

  if (!specialty) {
    return null;
  }

  const heroTitle = specialty.heroTitle || `Formation en ${specialty.title}`;

  const renderPageHeaderButton = (button, index) => {
    const className = `btn ${button.type === 'primary' ? 'btn-primary' : 'btn-outline-white'}`;
    const link = button.link || '#';
    const content = (
      <>
        {button.icon && <i className={button.icon} />}
        {button.text}
      </>
    );

    if (/^https?:\/\//i.test(link) || link.startsWith('mailto:') || link.startsWith('tel:')) {
      return (
        <a
          key={index}
          href={link}
          className={className}
          target={button.target || '_blank'}
          rel="noopener noreferrer"
        >
          {content}
        </a>
      );
    }

    if (link.startsWith('#')) {
      return (
        <a key={index} href={link} className={className}>
          {content}
        </a>
      );
    }

    return (
      <Link key={index} to={link} className={className}>
        {content}
      </Link>
    );
  };

  return (
    <div className="formations-page formation-specialty-page">
      <section className="page-header" style={{ position: 'relative', overflow: 'hidden' }}>
        {specialty.heroImage && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: 'hidden',
              zIndex: 0,
            }}
          >
            <img
              src={getMediaUrl(specialty.heroImage)}
              alt={specialty.title}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#58c4dc',
            opacity: 0.85,
            zIndex: 1,
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="page-header-content">
            <nav className="formation-specialty-breadcrumb" aria-label="Fil d'Ariane">
              <Link to="/">Accueil</Link>
              <span aria-hidden="true"> › </span>
              <Link to="/formations">Formations</Link>
              <span aria-hidden="true"> › </span>
              <span>{specialty.title}</span>
            </nav>
            <h1>{heroTitle}</h1>
            {specialty.description && <p>{specialty.description}</p>}
            {pageHeader.buttons?.length > 0 && (
              <div className="page-header-buttons">
                {pageHeader.buttons.map(renderPageHeaderButton)}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="formations-grid-section" id="courses">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {courses.length} Formation{courses.length > 1 ? 's' : ''} — {specialty.title}
            </h2>
            <p className="section-subtitle">
              Choisissez le programme qui correspond à votre niveau et vos objectifs
            </p>
          </div>

          <div className="formations-grid">
            {courses.map((course) => (
              <Link
                key={course.id}
                to={getCoursePath(specialtyPathSlug, course.slug)}
                className={`formation-card-compact formation-${course.color || specialty.color || 'blue'}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="formation-header-compact">
                  <div className="formation-icon-small">
                    <i className={course.icon || specialty.icon || 'fas fa-graduation-cap'} />
                  </div>
                  <div className="formation-title-info">
                    <h3>{course.title}</h3>
                    {course.subtitle && (
                      <span className="formation-level-badge">{course.subtitle}</span>
                    )}
                  </div>
                </div>
                <div className="formation-content-compact">
                  {course.instructor && (
                    <div className="formation-instructor-compact">
                      <i className="fas fa-chalkboard-teacher" />
                      <span>{course.instructor}</span>
                    </div>
                  )}
                  {course.description && (
                    <p className="formation-description-compact">{course.description}</p>
                  )}
                  {course.sessions?.[0] && (
                    <div className="formation-instructor-compact">
                      <i className="fas fa-calendar" />
                      <span>Prochaine session : {course.sessions[0].date}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {courses.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--gray)', padding: '2rem 0' }}>
              Aucune formation disponible pour cette spécialité pour le moment.{' '}
              <Link to="/formations">Retour aux formations</Link>
            </p>
          )}

          <div className="formation-specialty-back-bar">
            <Link to="/formations" className="formation-specialty-back-link">
              <span className="formation-specialty-back-icon" aria-hidden="true">
                <i className="fas fa-arrow-left" />
              </span>
              <span className="formation-specialty-back-text">
                <strong>Toutes les spécialités</strong>
                <span>Retour au catalogue — Implantologie, Orthodontie, Chirurgie orale…</span>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormationSpecialty;
